import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, poundsFromPence, formatErr } from "../lib/api";
import { useEvent } from "../lib/event";
import DashboardLayout from "../components/DashboardLayout";
import WizardBanner from "../components/WizardBanner";
import { Plus, Pencil, Trash2, X, Ticket as TicketIcon, ShoppingBag, ChevronLeft } from "lucide-react";
import { confirmDialog } from "../components/ConfirmDialog";
import OptionalCapInput from "../components/OptionalCapInput";

function Modal({ title, onClose, children, testid, wide = false }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center px-4 st-fade-up" data-testid={testid}>
      <div className={`st-card w-full ${wide ? "max-w-2xl" : "max-w-lg"} p-6 max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold">{title}</h2>
          <button onClick={onClose} className="text-[#8a8a8a] hover:text-white" data-testid="modal-close"><X size={18}/></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Labeled({ label, children, hint }) {
  return (
    <label className="block">
      <span className="text-xs text-[#8a8a8a]">{label}</span>
      <div className="mt-1">{children}</div>
      {hint && <p className="text-[11px] text-[#7a7a7a] mt-1">{hint}</p>}
    </label>
  );
}

function Toast({ text, kind, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 3000); return () => clearTimeout(t); }, [onDone]);
  return (
    <div className={`fixed top-5 right-5 z-[80] px-4 py-3 rounded-lg border st-fade-up ${
      kind === "err" ? "bg-[#2a1212] border-[#5b1919] text-[#fca5a5]" : "bg-[#0c1d12] border-[#1b3a25] text-[#86efac]"
    }`} data-testid={`toast-${kind}`}>
      {text}
    </div>
  );
}

export default function TicketTypesPage() {
  const { currentEvent, currentEventId } = useEvent();
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState(""); const [err, setErr] = useState("");
  const [ttModal, setTtModal] = useState(null);
  const [adModal, setAdModal] = useState(null);

  const load = async () => {
    if (!currentEventId) { setData(null); return; }
    try { const r = await api.get(`/events/${currentEventId}`); setData(r.data); }
    catch (e) { setErr("Failed to load event"); }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [currentEventId]);

  if (!currentEventId) {
    return <DashboardLayout title="Manage Ticket Types">
      <div className="st-card p-12 text-center" data-testid="no-event-prompt">
        <TicketIcon className="mx-auto text-[#dc2626]"/>
        <h3 className="text-xl font-bold mt-4">No event selected yet</h3>
      </div>
    </DashboardLayout>;
  }

  const tickets = data?.ticket_types || [];
  const addons = data?.addons || [];
  const hasNothing = data && (data.dates || []).length === 0 && tickets.length === 0;

  return (
    <DashboardLayout title="Manage Ticket Types"
                     subtitle={<><Link to="/dashboard/tickets" className="st-link text-sm" data-testid="back-to-tickets"><ChevronLeft size={12} className="inline -mt-0.5"/> Tickets</Link> · {currentEvent?.name}</>}>
      <WizardBanner eventId={currentEventId} show={hasNothing} />
      {msg && <Toast text={msg} kind="ok" onDone={() => setMsg("")} />}
      {err && <Toast text={err} kind="err" onDone={() => setErr("")} />}

      <section className="mb-10">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a]">Ticket types</h3>
            <p className="text-xs text-[#7a7a7a] mt-1">{tickets.length} configured</p>
          </div>
          <button className="st-btn" onClick={() => setTtModal("new")} data-testid="add-ticket-type-btn"><Plus size={14}/> Add ticket type</button>
        </div>
        {tickets.length === 0 ? (
          <div className="st-card p-10 text-center text-[#8a8a8a]" data-testid="tickets-empty">
            <TicketIcon className="mx-auto text-[#dc2626]"/>
            <p className="mt-3">No ticket types yet. Add one to start selling.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3" data-testid="ticket-types-grid">
            {tickets.map(t => <TicketTypeCard key={t.id} t={t}
                                              onEdit={() => setTtModal(t)}
                                              onToggle={async (v) => { await api.patch(`/events/${currentEventId}/ticket-types/${t.id}`, { is_active: v }); setMsg(v ? "Activated" : "Deactivated"); load(); }}
                                              onDelete={async () => {
                                                const ok = await confirmDialog({
                                                  title: "Delete ticket type?",
                                                  description: "This cannot be undone. Deleting is only allowed when no tickets of this type have been sold.",
                                                  confirmLabel: "Delete ticket type", destructive: true,
                                                });
                                                if (!ok) return;
                                                try { await api.delete(`/events/${currentEventId}/ticket-types/${t.id}`); setMsg("Deleted"); load(); }
                                                catch (e) { setErr(formatErr(e.response?.data?.detail) || "Delete failed"); }
                                              }} />)}
          </div>
        )}
      </section>

      <section>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a]">Add-on upsells</h3>
            <p className="text-xs text-[#7a7a7a] mt-1">{addons.length} configured</p>
          </div>
          <button className="st-btn" onClick={() => setAdModal("new")} data-testid="add-addon-btn"><Plus size={14}/> Add add-on</button>
        </div>
        {addons.length === 0 ? (
          <div className="st-card p-10 text-center text-[#8a8a8a]" data-testid="addons-empty">
            <ShoppingBag className="mx-auto text-[#dc2626]"/>
            <p className="mt-3">No add-ons yet. Boost AOV with optional extras.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3" data-testid="addons-grid">
            {addons.map(a => <AddonCard key={a.id} a={a}
                                        onEdit={() => setAdModal(a)}
                                        onToggle={async (v) => { await api.patch(`/events/${currentEventId}/addons/${a.id}`, { is_active: v }); setMsg(v ? "Activated" : "Deactivated"); load(); }}
                                        onDelete={async () => {
                                          const ok = await confirmDialog({
                                            title: "Delete add-on?",
                                            description: "This cannot be undone. Deleting is only allowed when no orders have purchased this add-on.",
                                            confirmLabel: "Delete add-on", destructive: true,
                                          });
                                          if (!ok) return;
                                          try { await api.delete(`/events/${currentEventId}/addons/${a.id}`); setMsg("Deleted"); load(); }
                                          catch (e) { setErr(formatErr(e.response?.data?.detail) || "Delete failed"); }
                                        }} />)}
          </div>
        )}
      </section>

      {ttModal && (
        <TicketTypeModal eventId={currentEventId} initial={ttModal === "new" ? null : ttModal}
                         onClose={() => setTtModal(null)}
                         onSaved={() => { setTtModal(null); setMsg("Saved"); load(); }} />
      )}
      {adModal && (
        <AddonModal eventId={currentEventId} initial={adModal === "new" ? null : adModal}
                    onClose={() => setAdModal(null)}
                    onSaved={() => { setAdModal(null); setMsg("Saved"); load(); }} />
      )}
    </DashboardLayout>
  );
}

function TicketTypeCard({ t, onEdit, onToggle, onDelete }) {
  return (
    <div className="st-card p-4" data-testid={`tt-card-${t.id}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h4 className="font-bold">{t.name}</h4>
            {(t.tiers || []).length > 0 && <span className="st-chip st-chip-red">Dynamic</span>}
            {!t.is_active && <span className="st-chip">Inactive</span>}
          </div>
          <div className="text-xl font-bold mt-1 text-[#dc2626]">{poundsFromPence(t.base_price_pence)}</div>
          <div className="text-xs text-[#8a8a8a] mt-1">
            {t.max_per_slot ? `Max ${t.max_per_slot}/slot` : "No per-type limit"}
            {(t.tiers || []).length > 0 && ` · ${t.tiers.length} tier${t.tiers.length === 1 ? "" : "s"}`}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <ToggleSwitch checked={t.is_active} onChange={onToggle} testid={`tt-toggle-${t.id}`} />
        </div>
      </div>
      <div className="flex gap-2 mt-4">
        <button className="st-btn st-btn-ghost text-xs" onClick={onEdit} data-testid={`tt-edit-${t.id}`}><Pencil size={12}/> Edit</button>
        <button className="st-btn st-btn-ghost text-xs text-[#fca5a5]" onClick={onDelete} data-testid={`tt-delete-${t.id}`}><Trash2 size={12}/> Delete</button>
      </div>
    </div>
  );
}

function AddonCard({ a, onEdit, onToggle, onDelete }) {
  return (
    <div className="st-card p-4" data-testid={`addon-card-${a.id}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h4 className="font-bold">{a.name}</h4>
            {!a.is_active && <span className="st-chip">Inactive</span>}
          </div>
          <div className="text-xl font-bold mt-1 text-[#dc2626]">{poundsFromPence(a.price_pence)}</div>
          <div className="text-xs text-[#8a8a8a] mt-1">{(a.max_per_order ?? a.max_quantity) ? `Max ${a.max_per_order ?? a.max_quantity} per order` : "Unlimited"}</div>
        </div>
        <ToggleSwitch checked={a.is_active} onChange={onToggle} testid={`ad-toggle-${a.id}`} />
      </div>
      <div className="flex gap-2 mt-4">
        <button className="st-btn st-btn-ghost text-xs" onClick={onEdit} data-testid={`ad-edit-${a.id}`}><Pencil size={12}/> Edit</button>
        <button className="st-btn st-btn-ghost text-xs text-[#fca5a5]" onClick={onDelete} data-testid={`ad-delete-${a.id}`}><Trash2 size={12}/> Delete</button>
      </div>
    </div>
  );
}

function ToggleSwitch({ checked, onChange, testid }) {
  return (
    <button onClick={() => onChange(!checked)} data-testid={testid}
            className={`w-10 h-6 rounded-full transition-colors relative ${checked ? "bg-[#dc2626]" : "bg-[#1f1f1f]"}`}>
      <span className={`block w-4 h-4 bg-white rounded-full absolute top-1 transition-all ${checked ? "left-5" : "left-1"}`} />
    </button>
  );
}

function TicketTypeModal({ eventId, initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const [name, setName] = useState(initial?.name || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [price, setPrice] = useState(((initial?.base_price_pence ?? 1500) / 100).toFixed(2));
  const [maxPer, setMaxPer] = useState(initial?.max_per_slot ?? null);
  const [maxOrder, setMaxOrder] = useState(initial?.max_per_order ?? null);
  const [saleStart, setSaleStart] = useState(initial?.sale_start_at?.slice(0, 16) || "");
  const [saleEnd, setSaleEnd] = useState(initial?.sale_end_at?.slice(0, 16) || "");
  const [active, setActive] = useState(initial?.is_active ?? true);
  const [tiersOn, setTiersOn] = useState((initial?.tiers || []).length > 0);
  const [tiers, setTiers] = useState(initial?.tiers || []);
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);

  const addTier = () => setTiers([...tiers, { cutoff_date: "", discount_type: "amount", discount_value: 200 }]);
  const updTier = (i, k, v) => setTiers(tiers.map((t, j) => j === i ? { ...t, [k]: v } : t));
  const delTier = (i) => setTiers(tiers.filter((_, j) => j !== i));

  const save = async () => {
    setErr(""); setBusy(true);
    try {
      const payload = {
        name, base_price_pence: Math.round(parseFloat(price || "0") * 100),
        description: description.trim() || null,
        max_per_slot: maxPer ?? null,
        max_per_order: maxOrder ?? null,
        sale_start_at: saleStart || null, sale_end_at: saleEnd || null,
        is_active: active,
        tiers: tiersOn ? tiers : [],
      };
      if (isEdit) await api.patch(`/events/${eventId}/ticket-types/${initial.id}`, payload);
      else await api.post(`/events/${eventId}/ticket-types`, payload);
      onSaved();
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={isEdit ? "Edit ticket type" : "Add ticket type"} onClose={onClose} testid="ticket-type-modal" wide>
      <div className="space-y-3">
        <Labeled label="Name"><input className="st-input" value={name} onChange={(e) => setName(e.target.value)} data-testid="tt-name" /></Labeled>
        <Labeled label="Description (optional)" hint="Shown under the ticket name at checkout. Leave blank to hide.">
          <textarea className="st-input min-h-[60px] resize-y" value={description} maxLength={200} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. Skip the queue · Includes a free drink" data-testid="tt-description" />
        </Labeled>
        <div className="grid grid-cols-2 gap-3">
          <Labeled label="Base price (£)"><input type="number" step="0.01" className="st-input" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="tt-price" /></Labeled>
          <Labeled label="Max per slot (optional)" hint="Caps total sold per slot"><OptionalCapInput value={maxPer} onChange={setMaxPer} testid="tt-maxper" /></Labeled>
          <Labeled label="Max per order (optional)" hint="Caps quantity in one booking"><OptionalCapInput value={maxOrder} onChange={setMaxOrder} testid="tt-maxorder" /></Labeled>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Labeled label="Sale starts" hint="Optional"><input type="datetime-local" className="st-input" value={saleStart} onChange={(e) => setSaleStart(e.target.value)} /></Labeled>
          <Labeled label="Sale ends" hint="Optional"><input type="datetime-local" className="st-input" value={saleEnd} onChange={(e) => setSaleEnd(e.target.value)} /></Labeled>
        </div>

        <div className="st-card p-4 bg-[#0c0c0c]">
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input type="checkbox" checked={tiersOn} onChange={(e) => setTiersOn(e.target.checked)} data-testid="tt-tiers-toggle" />
            Enable dynamic pricing tiers
          </label>
          {tiersOn && (
            <div className="mt-3 space-y-2">
              {tiers.map((t, i) => {
                const basePence = Math.round(parseFloat(price || "0") * 100);
                const calc = t.discount_type === "amount" ? basePence - Number(t.discount_value)
                  : basePence - Math.round(basePence * Number(t.discount_value) / 100);
                return (
                  <div key={i} className="grid grid-cols-[1fr_110px_100px_auto_auto] gap-2 items-center">
                    <input type="date" className="st-input" value={t.cutoff_date} onChange={(e) => updTier(i, "cutoff_date", e.target.value)} />
                    <select className="st-input" value={t.discount_type} onChange={(e) => updTier(i, "discount_type", e.target.value)}>
                      <option value="amount">£ off</option><option value="percentage">% off</option>
                    </select>
                    <input type="number" className="st-input" value={t.discount_value} onChange={(e) => updTier(i, "discount_value", Number(e.target.value))} />
                    <div className="text-xs text-[#dc2626] font-bold w-16 text-right">{poundsFromPence(Math.max(0, calc))}</div>
                    <button className="text-[#dc2626]" onClick={() => delTier(i)}><Trash2 size={14}/></button>
                  </div>
                );
              })}
              <button className="st-btn st-btn-ghost text-xs" onClick={addTier} data-testid="tt-add-tier"><Plus size={12}/> Add tier</button>
            </div>
          )}
        </div>

        <label className="flex items-center gap-2 cursor-pointer text-sm">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} data-testid="tt-active" />
          Active (available for sale)
        </label>
        {isEdit && <p className="text-[11px] text-[#fbbf24]">Changing price affects future orders only. Existing orders are unaffected.</p>}
        {err && <div className="text-sm text-[#fca5a5]">{err}</div>}
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <button className="st-btn st-btn-ghost" onClick={onClose}>Cancel</button>
        <button className="st-btn" disabled={busy || !name} onClick={save} data-testid="tt-save">{busy ? "Saving…" : "Save"}</button>
      </div>
    </Modal>
  );
}

function AddonModal({ eventId, initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const [name, setName] = useState(initial?.name || "");
  const [price, setPrice] = useState(((initial?.price_pence ?? 500) / 100).toFixed(2));
  const [maxQty, setMaxQty] = useState(initial?.max_per_order ?? initial?.max_quantity ?? null);
  const [active, setActive] = useState(initial?.is_active ?? true);
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);

  const save = async () => {
    setErr(""); setBusy(true);
    try {
      const payload = {
        name, price_pence: Math.round(parseFloat(price || "0") * 100),
        max_per_order: maxQty ?? null,
        max_quantity: maxQty ?? null,
        is_active: active,
      };
      if (isEdit) await api.patch(`/events/${eventId}/addons/${initial.id}`, payload);
      else await api.post(`/events/${eventId}/addons`, payload);
      onSaved();
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={isEdit ? "Edit add-on" : "Add add-on"} onClose={onClose} testid="addon-modal">
      <div className="space-y-3">
        <Labeled label="Name"><input className="st-input" value={name} onChange={(e) => setName(e.target.value)} data-testid="ad-name" /></Labeled>
        <div className="grid grid-cols-2 gap-3">
          <Labeled label="Price (£)"><input type="number" step="0.01" className="st-input" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="ad-price" /></Labeled>
          <Labeled label="Max per order (optional)"><OptionalCapInput value={maxQty} onChange={setMaxQty} testid="ad-maxqty" /></Labeled>
        </div>
        <label className="flex items-center gap-2 cursor-pointer text-sm">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} data-testid="ad-active" />
          Active
        </label>
        {err && <div className="text-sm text-[#fca5a5]">{err}</div>}
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <button className="st-btn st-btn-ghost" onClick={onClose}>Cancel</button>
        <button className="st-btn" disabled={busy || !name} onClick={save} data-testid="ad-save">{busy ? "Saving…" : "Save"}</button>
      </div>
    </Modal>
  );
}
